import { z, type ZodTypeAny } from 'zod';
import type { FieldMeta } from '../meta/types';
import { getPath, setPath } from '../meta/path';
import { blankFor, typeDef } from '../fields/types';

// FieldMeta[] → zod object. Required + nullable + type constraints; server-side validation is
// still authoritative (its field errors are mapped back onto the form by AutoForm).

const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const dateRe = /^\d{4}-\d{2}-\d{2}$/;

function base(f: FieldMeta): ZodTypeAny {
  switch (f.type) {
    case 'text':
    case 'textarea':
    case 'richtext':
    case 'phone':
      return z.string();
    case 'email':
      return z.string().refine((s) => s === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s), 'Enter a valid email');
    case 'url':
      return z.string().refine((s) => s === '' || /^https?:\/\/\S+$/i.test(s), 'Enter a valid URL (https://…)');
    case 'number':
      return z.number({ error: 'Enter a number' });
    case 'integer':
    case 'money':
    case 'duration':
      return z.number({ error: f.type === 'money' ? 'Enter an amount' : 'Enter a whole number' }).int();
    case 'boolean':
      return z.boolean();
    case 'date':
      return z.string().regex(dateRe, 'Enter a date');
    case 'datetime':
      return z.string().refine((s) => !Number.isNaN(new Date(s).getTime()), 'Enter a date and time');
    case 'enum':
      return f.enum && f.enum.length ? z.enum(f.enum.map((o) => o.value) as [string, ...string[]]) : z.string();
    case 'uuid':
    case 'relation':
      return z.string().regex(uuidRe, 'Pick a value').or(z.string().min(1));
    case 'json':
      return z.unknown();
    case 'tags':
      return z.array(z.string());
    case 'color':
      return z.string().regex(/^#[0-9a-f]{6}$/i, 'Use a hex colour like #1f3fd6');
  }
}

/**
 * FieldMeta[] → zod object. Dotted names (`custom_fields.colour`) become nested objects, so the
 * form state mirrors the row: `{ custom_fields: { colour } }`.
 */
export function zodFromMeta(fields: FieldMeta[]): z.ZodObject<Record<string, ZodTypeAny>> {
  const tree: Record<string, unknown> = {};
  for (const f of fields) {
    let s = base(f);
    if (f.required) {
      if (typeDef(f).textual) s = (s as z.ZodString).min(1, `${f.label} is required`);
      setPath(tree, f.name, s);
    } else {
      setPath(tree, f.name, s.nullable().optional());
    }
  }
  const build = (node: Record<string, unknown>): z.ZodObject<Record<string, ZodTypeAny>> =>
    z.object(Object.fromEntries(Object.entries(node).map(([k, v]) => [k, v instanceof z.ZodType ? v : build(v as Record<string, unknown>).optional()])));
  return build(tree);
}

/** Empty strings become null for optional fields so the API gets "no value", not "". */
/**
 * Form values → request body. An untouched optional field is '' (text) or null (pickers,
 * dates, money). On CREATE that means "not provided": the key is OMITTED, because resource
 * schemas declare optional inputs as `.optional()` (undefined), not `.nullable()`, and sending
 * null yields `400 Expected string, received null` for every form with an empty optional
 * field. On EDIT a cleared field is a real instruction ("unset it"), so it travels as null.
 */
export function normalizeValues(fields: FieldMeta[], values: Record<string, unknown>, mode: 'create' | 'edit' = 'edit'): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    let v = getPath(values, f.name);
    if (v === '' && !f.required) v = null;
    if (v === undefined) continue;
    if (v === null && mode === 'create') continue;
    setPath(out, f.name, v);
  }
  return out;
}

/** Default values for a create form (or an edit form's row): FieldMeta.default, a shape's empty value, else the type's blank — nested for dotted names. */
export function defaultsFromMeta(fields: FieldMeta[], initial?: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const given = initial ? getPath(initial, f.name) : undefined;
    if (initial && (f.name in initial || given !== undefined)) {
      setPath(out, f.name, given);
      continue;
    }
    setPath(out, f.name, blankFor(f));
  }
  return out;
}
